import { useText } from "@/context/TextContext";
import { MOBILE_BASE } from "@/theme/theme";
import { pxToVw } from "@/utils/pxToVw";
import { Box, Grid, Typography } from "@mui/material";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

export default function ExpandibleText({
  title,
  subtitle,
  description,
  descriptionExpanded,
  images = [],
  index,
}: {
  title: string;
  subtitle?: string;
  description: string;
  descriptionExpanded?: string;
  images?: { alt: string; src: string; width: number; height: number }[];
  index?: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const boxRef = useRef<HTMLDivElement>(null);

  const calculateHeight = () => {
    if (boxRef.current) {
      const height = boxRef.current.scrollHeight;
      return height;
    }
    return 0;
  };

  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    const height = calculateHeight();
    setMaxHeight(isExpanded ? `${height}px` : "0px");
  }, [isExpanded]);

  descriptionExpanded = descriptionExpanded?.replace(description, "");

  const llegirmes = useText("general.llegirmes");
  const llegirmenys = useText("general.llegirmenys");

  return (
    <Box mb={{ xs: pxToVw(40, MOBILE_BASE), md: pxToVw(60) }}>
      <Typography
        variant="titulo"
        color="marron"
        display="block"
        component={index === 0 ? "h1" : "h2"}
        sx={{ textTransform: "uppercase" }}
        dangerouslySetInnerHTML={{ __html: title }}
        whiteSpace={"pre-line"}
        mb={{ xs: pxToVw(6, MOBILE_BASE), md: pxToVw(8) }}
      />
      <Box>
        {description && (
          <Typography
            variant="body3"
            color="marron"
            display="block"
            className="historiaText"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
        <Box
          sx={{
            transition: "all 1s",
            maxHeight: isExpanded ? maxHeight : "0px",
            overflow: "hidden",
          }}
          ref={boxRef}
        >
          {descriptionExpanded && (
            <Typography
              variant="body3"
              color="marron"
              display="block"
              className="historiaText"
              dangerouslySetInnerHTML={{
                __html: descriptionExpanded,
              }}
            />
          )}
          {images.length > 0 && (
            <Grid
              container
              spacing={{
                xs: pxToVw(10, MOBILE_BASE),
                md: pxToVw(20),
              }}
              mt={{ xs: pxToVw(10, MOBILE_BASE), md: pxToVw(27) }}
            >
              {images.map((image, imgIndex) => (
                <Grid
                  key={imgIndex}
                  size={{ xs: 6, md: 4 }}
                  offset={{ xs: 6, md: 4 }}
                >
                  <Image
                    alt={image.alt}
                    src={image.src}
                    width={image.width}
                    height={image.height}
                  />
                  <Typography
                    variant="caption1"
                    color="warmaa"
                    display="block"
                    mb={{ xs: pxToVw(6, MOBILE_BASE), md: pxToVw(8) }}
                  >
                    {image.alt}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
      {/* If descriptionExpanded exists */}
      {(descriptionExpanded || images.length > 0) && (
        <Typography
          variant="label1"
          color="warmaa"
          display="block"
          mt={{ xs: pxToVw(15, MOBILE_BASE), md: pxToVw(20) }}
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            cursor: "pointer",
          }}
        >
          {isExpanded ? llegirmenys + " ↑" : llegirmes + " ↓"}
        </Typography>
      )}
    </Box>
  );
}
